import { Fragment, memo } from 'react'
import { Form, Radio, Space } from 'antd'
import { Select } from '../../../../../components/Select'
import { Input } from '../../../../../components/Input'
import { Button, BUTTON_TYPES } from '../../../../../components/Button'
import { Typography } from '../../../../../components/Typography'
import { useRebateConditionsFormContext } from '../Providers/RebateConditionsFormProvider'
import {
  getMarketShareThresholdFieldName,
  isEmptyValue,
  percentageRebateValidator,
  positiveNumberValidator
} from '../utils'
import { ReactComponent as Plus16 } from 'assets/svg/Plus16.svg'
import { ReactComponent as Clos16 } from 'assets/svg/Clos16.svg'
import { BTN_TXT, LABELS, PLACEHOLDERS } from '../../../../../constants'
import { PROPS_INPUT_NUMBER, REBATE_CONDITIONS_TYPE } from '../constants'
import { useContractDetailsContext } from 'features/ContractDetails/Providers/ContractDetailsContextProvider'
import {
  DEFAULT_HOSPITAL_VALUES,
  HOSPITAL_OPTIONS,
  PURCHASE_VOLUME_THRESHOLD_MEASURED_IN,
  THRESHOLD_MEASURED_IN_OPTIONS
} from './constants'
import { TOption } from '../../../../../components/Select/types'
import { MarketShareRebateConditionProps, RebateTier } from './types'
import './styles.scss'
import { Separator } from 'components/Separator/Separator'
import { RowLayout } from 'features/MyCommunity/Vendors/VendorCreation/General/VendorForm/RowLayout'
import { useContractDetailsPopup } from 'features/ContractDetails/Providers/ContractDetailsPopupProvider'
import EditRebateTiersPopup from './EditRebateTiersPopup/EditRebateTiersPopup'
import TiersEmptyState from './TiersEmptyState/TiersEmptyState'
import TiersView from './TiersView/TiersView'
import {
  ACTIONS,
  SUBJECTS,
  contractSubject
} from 'features/ContractDetails/Abilities'
import { useContractDetailsAbility } from 'features/ContractDetails/Providers/ContractDetailsAbilityProvider'

const MarketShareRebateCondition = ({
  applyToHospitals,
  handleChangeForm,
  hospitalRebates,
  form
}: MarketShareRebateConditionProps) => {
  const { hospitals, thresholdPeriodOptions, saveFormAndUpdateTiers, tiers } =
    useRebateConditionsFormContext()
  const { details } = useContractDetailsContext().state
  const selectedHospitals = hospitalRebates?.filter((i) => !!i?.hospital) || []

  const getFilteredHospitals = (options: TOption[], idx: number) =>
    options.filter(
      (item) =>
        !(
          selectedHospitals?.find((i) => i?.hospital === item.value) &&
          selectedHospitals?.[idx]?.hospital !== item.value
        )
    )

  const isTiered = Form.useWatch('is_tiered', form)
  const thresholdMeasuredIn = Form.useWatch('threshold_measured_in', form)
  const { editRebateTiersPopup } = useContractDetailsPopup()
  const isPurchaseVolume =
    thresholdMeasuredIn === PURCHASE_VOLUME_THRESHOLD_MEASURED_IN
  const thresholdFieldName =
    getMarketShareThresholdFieldName(thresholdMeasuredIn)
  const savedThresholdMeasuredIn =
    details.rebate_type === REBATE_CONDITIONS_TYPE.MARKET_SHARE_REBATE
      ? details.rebate?.threshold_measured_in ??
        THRESHOLD_MEASURED_IN_OPTIONS[0].value
      : undefined
  const showSavedTiers = thresholdMeasuredIn === savedThresholdMeasuredIn
  const visibleTiers = showSavedTiers ? tiers : []
  const hasTiers = !!visibleTiers.length

  const ability = useContractDetailsAbility()

  const canEditTiers = ability.can(
    ACTIONS.EDIT,
    contractSubject(SUBJECTS.MARKET_SHARE_TIERS, details)
  )

  const openTiersPopup = () => editRebateTiersPopup.actions.open()

  const onSaveTiers = (nextTiers: RebateTier[]) =>
    saveFormAndUpdateTiers(
      showSavedTiers
        ? nextTiers
        : nextTiers.map(({ threshold, amount }) => ({
            uuid: null,
            threshold,
            amount
          }))
    )

  return (
    <>
      <Separator />
      <RowLayout title="Rebate conditions">
        <div className="market-share-rebate column gap-24">
          <div className="row">
            <Select
              options={THRESHOLD_MEASURED_IN_OPTIONS}
              propsItem={{
                name: 'threshold_measured_in',
                label: LABELS.THRESHOLD_MEASURED_IN
              }}
              propsSelect={{
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                onChange: (value: string) => {
                  const nextField = getMarketShareThresholdFieldName(value)

                  if (nextField !== thresholdFieldName) {
                    form.setFieldsValue({
                      [nextField]:
                        form.getFieldValue(thresholdFieldName) ?? null,
                      [thresholdFieldName]: null
                    })
                  }

                  handleChangeForm()
                }
              }}
            />
          </div>
          {!isTiered && (
            <div className="row gap-24">
              <Input.Number
                propsItem={{
                  name: thresholdFieldName,
                  label:
                    thresholdMeasuredIn ===
                    PURCHASE_VOLUME_THRESHOLD_MEASURED_IN
                      ? LABELS.THRESHOLD_DOLLARS
                      : LABELS.THRESHOLD,
                  rules: [
                    {
                      required: applyToHospitals,
                      validator: applyToHospitals
                        ? (_, value) => {
                            if (isEmptyValue(value)) {
                              return Promise.reject(
                                new Error('Threshold is required')
                              )
                            }
                            return thresholdMeasuredIn === 'purchase_volume'
                              ? positiveNumberValidator(_, value)
                              : percentageRebateValidator(_, value)
                          }
                        : undefined
                    }
                  ]
                }}
                propsInputNumber={{
                  ...PROPS_INPUT_NUMBER,
                  disabled: !applyToHospitals
                }}
              />
              <Input.Number
                propsItem={{
                  name: 'amount',
                  label: LABELS.REBATE_AMOUNT,
                  rules: [
                    {
                      validator: (_, value) => {
                        if (isEmptyValue(value)) {
                          return Promise.reject(
                            new Error('Rebate amount is required')
                          )
                        }
                        return percentageRebateValidator(_, value)
                      }
                    }
                  ]
                }}
                propsInputNumber={PROPS_INPUT_NUMBER}
              />
            </div>
          )}
          <Select
            options={thresholdPeriodOptions}
            propsItem={{
              name: 'period_threshold',
              label: LABELS.PERIOD_THRESHOLD
            }}
            propsSelect={{
              placeholder: PLACEHOLDERS.PLEASE_SELECT,
              onChange: handleChangeForm
            }}
          />
        </div>
      </RowLayout>
      {isTiered && (
        <>
          <Separator />
          <div className="market-share-tiers">
            <RowLayout
              title="Tiers"
              subtitle="Base ladder, applies to every quarter of the contract term."
            >
              {hasTiers ? (
                <TiersView
                  tiers={visibleTiers}
                  isPurchaseVolume={isPurchaseVolume}
                  canEditTiers={canEditTiers}
                  onEdit={openTiersPopup}
                />
              ) : (
                <TiersEmptyState
                  onAdd={openTiersPopup}
                  canAddTiers={canEditTiers}
                />
              )}
            </RowLayout>
          </div>
        </>
      )}
      <Separator />
      <RowLayout title="Applicability">
        <Form.Item
          label={LABELS.APPLY_TO_ALL_HOSPITALS}
          name="apply_to_all_hospitals"
          className="market-share-rebate__hospital-option"
          colon={false}
        >
          <Radio.Group>
            <Space direction="vertical">
              {HOSPITAL_OPTIONS.map((i) => (
                <Radio key={i.label} value={i.value} disabled={true}>
                  <Typography.Body1>{i.label}</Typography.Body1>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Form.Item>
        {!applyToHospitals && (
          <Form.List name="hospitals_rebates">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }, idx) => (
                  <Fragment key={key}>
                    <div className="market-share-rebate__hospital-rebate">
                      <Select
                        options={getFilteredHospitals(hospitals, idx)}
                        propsItem={{
                          ...rest,
                          name: [name, 'hospital'],
                          label: LABELS.HOSPITAL,
                          rules: [
                            {
                              required: true,
                              message: 'Hospital is required'
                            }
                          ]
                        }}
                        propsSelect={{
                          placeholder: PLACEHOLDERS.PLEASE_SELECT,
                          onChange: handleChangeForm
                        }}
                      />
                      {hospitalRebates.length > 1 && (
                        <Button.Icon
                          type={BUTTON_TYPES.GHOST}
                          icon={<Clos16 />}
                          className="market-share-rebate__remove-hospital-btn"
                          onClick={() => {
                            remove(name)
                            handleChangeForm()
                          }}
                        />
                      )}
                    </div>
                    <div className="row">
                      <Input.Number
                        propsItem={{
                          ...rest,
                          name: [name, 'purchase_volume_threshold'],
                          label: LABELS.HOSPITAL_TARGET,
                          rules: [
                            {
                              validator: percentageRebateValidator
                            }
                          ]
                        }}
                        propsInputNumber={PROPS_INPUT_NUMBER}
                      />
                    </div>
                  </Fragment>
                ))}
                {hospitalRebates.length !== hospitals.length && (
                  <Button
                    className="market-share-rebate__add-btn"
                    type={BUTTON_TYPES.GHOST}
                    icon={<Plus16 />}
                    onClick={() => {
                      add({ ...DEFAULT_HOSPITAL_VALUES })
                      handleChangeForm()
                    }}
                    upperCase
                  >
                    {BTN_TXT.ADD_HOSPITAL}
                  </Button>
                )}
              </>
            )}
          </Form.List>
        )}
      </RowLayout>
      <EditRebateTiersPopup
        thresholdMeasuredIn={thresholdMeasuredIn}
        tiers={visibleTiers}
        onSave={onSaveTiers}
      />
    </>
  )
}

export default memo(MarketShareRebateCondition)
